import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// ──────────────────────────────────────────────────────────────────────
// Status enums (PRD §4 + §5.3)
// ──────────────────────────────────────────────────────────────────────

// Aligned with back-office `deliveryStatus` enum (full source-of-truth list,
// including transient sub-statuses that appear in the tracking timeline).
export type DeliveryStatus =
  | 'PENDING_PAYMENT'
  | 'EXPIRED'
  | 'QUEUEING'
  | 'ALLOCATING'
  | 'PENDING_PICKUP'
  | 'PICKING_UP'
  | 'ARRIVED_AT_PICKUP_POINT'
  | 'CREATED'
  | 'PREPARING'
  | 'PENDING_DELIVERY'
  | 'IN_DELIVERY'
  | 'ARRIVED_AT_DESTINATION'
  | 'VERIFIED'
  | 'NOT_VERIFIED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'IN_RETURN'
  | 'PENDING_RETURN'
  | 'FAILED_IN_RETURN'
  | 'RETURN_TO_HUB'
  | 'ARRIVED_AT_HUB'
  | 'ON_HOLD'
  | 'ARRIVED_AT_RETURN_POINT'
  | 'RETURNED'
  | 'DISPOSED'
  | 'REJECTED';

export type InternalReasonCodeState =
  | 'PREPARING'
  | 'PRE_PICKUP'
  | 'POST_PICKUP'
  | 'IN_RETURN'
  | 'UNCATEGORIZED';

export type SlaStatus = 'ON_TIME' | 'LATE' | null;

export type CodPaymentMethod = 'CASH' | 'TRANSFER' | string;

export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  PENDING_PAYMENT: 'Pending Payment',
  EXPIRED: 'Expired',
  QUEUEING: 'Queueing',
  ALLOCATING: 'Allocating',
  PENDING_PICKUP: 'Pending Pickup',
  PICKING_UP: 'Picking Up',
  ARRIVED_AT_PICKUP_POINT: 'Arrived at Pickup Point',
  CREATED: 'Created',
  PREPARING: 'Preparing',
  PENDING_DELIVERY: 'Pending Delivery',
  IN_DELIVERY: 'In Delivery',
  ARRIVED_AT_DESTINATION: 'Arrived at Destination',
  VERIFIED: 'Verified',
  NOT_VERIFIED: 'Not Verified',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  IN_RETURN: 'In Return',
  PENDING_RETURN: 'Pending Return',
  FAILED_IN_RETURN: 'Failed Return',
  RETURN_TO_HUB: 'Return to Hub',
  ARRIVED_AT_HUB: 'Arrived at Hub',
  ON_HOLD: 'On Hold',
  ARRIVED_AT_RETURN_POINT: 'Arrived at Return Point',
  RETURNED: 'Returned',
  DISPOSED: 'Disposed',
  REJECTED: 'Rejected',
};

// ──────────────────────────────────────────────────────────────────────
// List response shapes (PRD §6.1 / §6.2)
// ──────────────────────────────────────────────────────────────────────

export interface DeliveryListItem {
  deliveryID: string;
  createdAt: string;
  status: DeliveryStatus;
  internalState: InternalReasonCodeState | null;
  pickupAt: string | null;
  completedAt: string | null;
  max_pickup_at: string | null;
  max_completion_at: string | null;
  pickup_sla_status: SlaStatus;
  complete_sla_status: SlaStatus;
  businessUnit: string | null;
  provider: { id: number; name: string } | null;
  providerOutlet: { id: number; name: string } | null;
  courier: { id: number; name: string; phone: string } | null;
  cashOnDelivery: {
    paymentMethod: CodPaymentMethod;
    amount: number;
  } | null;
  recipient: {
    firstName: string;
    lastName: string | null;
  } | null;
  schedule: {
    pickupTimeFrom: string;
    pickupTimeTo: string;
  } | null;
}

export interface DeliveryListResponse {
  data: DeliveryListItem[];
  pagination: {
    total: number;
    last_page: number;
    current_page: number;
  };
}

// ──────────────────────────────────────────────────────────────────────
// Detail response shape (PRD §6.3 / §8)
// ──────────────────────────────────────────────────────────────────────

export interface DeliveryDetail {
  deliveryID: string;
  providerID: number;
  providerOrderID: string | null;
  status: DeliveryStatus;
  internalState: InternalReasonCodeState | null;
  isCancellable: boolean;
  isReassignable: boolean;
  cancelledReason: string | null;
  failedReason: string | null;
  createdAt: string;
  pickupAt: string | null;
  completedAt: string | null;
  max_pickup_at: string | null;
  max_completion_at: string | null;
  pickup_sla_status: SlaStatus;
  complete_sla_status: SlaStatus;
  trackingURL: string | null;
  revenueStream: string | null;
  additionalInfo: Record<string, string> | null;
  policies: { oneTimeCode: string | null } | null;
  client: { id: number; name: string } | null;
  clientOutlet: { id: number; name: string } | null;
  courier: { id: number; name: string; phone: string } | null;
  sender: {
    firstName: string;
    lastName: string | null;
    phone: string | null;
  } | null;
  recipient: {
    firstName: string;
    lastName: string | null;
    phone: string | null;
  } | null;
  quote: {
    distance: number | null;
    service: { name: string } | null;
    origin: {
      address: string;
      /**
       * Rider-facing pickup instruction. Rides along from the shipment
       * import's `Origin Notes` column, so it is absent on deliveries created
       * any other way.
       */
      notes?: string | null;
      coordinates: { latitude: number; longitude: number };
    } | null;
    destination: {
      address: string;
      /** Rider-facing drop-off instruction (`Destination Notes` at import). */
      notes?: string | null;
      coordinates: { latitude: number; longitude: number };
    } | null;
    packages: Array<{ name: string; weight: number }>;
  } | null;
  cashOnDelivery: {
    codPaymentMethod: string;
    amount: number;
    isPaid: boolean;
    codConfirmedBy: { name: string } | null;
  } | null;
  media: Array<{ type: 'PICKUP_PROOF' | 'DELIVERY_PROOF'; url: string }>;
  /**
   * Shipment tracking timeline. Each entry mirrors the backoffice
   * `VerticalProgressBar` shape so we can render badges, proof avatars,
   * driver changes, notes, and the "Updated By {name} ({source})" line.
   */
  timeline: Array<{
    status: DeliveryStatus;
    createdAt: string;
    notes: string | null;
    media?: Array<{ url: string; type?: string }> | null;
    driver?: { id?: number | string; name: string } | null;
    previousDriver?: { id?: number | string; name: string } | null;
    createdBy: {
      name: string;
      source:
        | 'WEB'
        | 'CUSTOMER'
        | 'DRIVER'
        | 'INTERNAL-SERVICE'
        | 'PROVIDER-H2H'
        | 'PROVIDER-USER'
        | 'CLIENT-USER'
        | 'SYSTEM'
        | string
        | null;
    };
  }>;
  schedule?: { pickupTimeFrom: string; pickupTimeTo: string } | null;
  businessUnit?: string | null;
}

export interface DeliveryListParams {
  page?: number;
  size?: number;
  search?: string;
  providerIDs?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
  driverID?: string;
  providerOutletIDs?: string;
  codPaymentMethod?: string;
  businessUnit?: string;
}

export type DownloadMode = 'BILLING' | 'TRACKING';

export interface DeliveryDownloadParams {
  /** Provider / client ID — required, used as path param */
  providerID: string;
  mode: DownloadMode;
  email: string;
  /** ISO date string YYYY-MM-DD (local timezone). */
  start_date: string;
  /** ISO date string YYYY-MM-DD (local timezone). */
  end_date: string;
  // Optional active-filter params. Names match what the backend expects
  // on the download endpoint (verified against the backoffice
  // implementation, which is the live source of truth).
  status?: string;
  /**
   * Driver filter. The download endpoint expects `driverID` (singular,
   * capital D), NOT `drivers` (which is the list-endpoint convention).
   */
  driverID?: string;
  /**
   * Outlet filter. Backoffice sends both casings of the same value
   * because the backend has historically been inconsistent. We mirror
   * that so the filter never gets silently dropped.
   */
  providerOutletIDs?: string;
  providerOutletIds?: string;
  businessUnit?: string;
}

export interface DeliveryDownloadResponse {
  isFile?: boolean;
  message?: string;
}

// ──────────────────────────────────────────────────────────────────────
// API
// ──────────────────────────────────────────────────────────────────────

type Envelope<T> = { status?: number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

/** Pick a sensible file extension from a Content-Type when the server
 *  didn't send a `Content-Disposition` filename. Mirrors backoffice's
 *  `Data_Delivery_{timestamp}.{ext}` convention. */
function buildFallbackFilename(contentType: string): string {
  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .slice(0, -5);
  if (contentType.includes('text/csv')) {
    return `Data_Delivery_${timestamp}.csv`;
  }
  if (
    contentType.includes(
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) ||
    contentType.includes('application/vnd')
  ) {
    return `Data_Delivery_${timestamp}.xlsx`;
  }
  if (contentType.includes('application/zip')) {
    return `Data_Delivery_${timestamp}.zip`;
  }
  return `Data_Delivery_${timestamp}`;
}

export const deliveriesAPI = {
  list: async (
    params: DeliveryListParams = {}
  ): Promise<DeliveryListResponse> => {
    try {
      const response = await Axios.get('/mgmt/v1/deliveries', { params });
      // The /v1/deliveries endpoint returns { data, pagination } directly,
      // matching the PRD shape — but tolerate a wrapped envelope too.
      const body = response.data;
      if (body && typeof body === 'object' && 'pagination' in body) {
        return body as DeliveryListResponse;
      }
      return unwrap<DeliveryListResponse>(body);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Download delivery data as a file or receive a queued-job message.
   *
   * GET /v1/deliveries/providers/:providerID/orders/downloads
   *
   * When `response.isFile` is true the caller should trigger a blob download;
   * otherwise display `response.message` as a success notification.
   */
  download: async (
    params: DeliveryDownloadParams
  ): Promise<DeliveryDownloadResponse> => {
    const { providerID, ...query } = params;
    try {
      // No `validateStatus` override — let axios throw on 4xx/5xx so the
      // outer catch can surface a proper error. (A 4xx with a JSON body
      // used to silently route through the "queued job" branch and
      // appear as a success toast.)
      const response = await Axios.get(
        `/v1/deliveries/providers/${providerID}/orders/downloads`,
        {
          params: { ...query, platform: 'LOGISTIC' },
          responseType: 'blob',
        }
      );

      const contentType: string = String(
        response.headers?.['content-type'] ?? ''
      );

      const isCsv = contentType.includes('text/csv');
      const isXlsx = contentType.includes(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      );
      const isOtherVnd = !isXlsx && contentType.includes('application/vnd');
      const isZip = contentType.includes('application/zip');
      const isOctet = contentType.includes('application/octet-stream');
      const isFile = isCsv || isXlsx || isOtherVnd || isZip || isOctet;

      if (isFile) {
        const url = URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;

        // Prefer the server-supplied filename. Fall back to a
        // content-type-aware name so xlsx never gets saved as `.csv`.
        const disposition: string = String(
          response.headers?.['content-disposition'] ?? ''
        );
        const match = disposition.match(
          /filename[^;=\n]*=(['"]?)([^'";\n]+)\1/
        );
        a.download = match ? match[2] : buildFallbackFilename(contentType);

        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return { isFile: true };
      }

      // Otherwise the server returned JSON (queued-job message path).
      const text = await (response.data as Blob).text();
      const json = JSON.parse(text) as DeliveryDownloadResponse;
      return json;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** GET mgmt/v1/deliveries/:id */
  getDetail: async (id: string): Promise<DeliveryDetail> => {
    try {
      const response = await Axios.get(`/mgmt/v1/deliveries/${id}`);
      const body = response.data;
      // Unwrap envelope if present
      return (body?.data ?? body) as DeliveryDetail;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** PATCH /v1/deliveries/:id/status */
  updateStatus: async (
    id: string,
    status: DeliveryStatus
  ): Promise<{ message?: string }> => {
    try {
      const response = await Axios.patch(`/v1/deliveries/${id}/status`, {
        status,
      });
      return response.data?.data ?? response.data ?? {};
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** POST /mgmt/v1/deliveries/:id/cancel */
  cancel: async (
    id: string,
    payload: { notes: string; reasonCode: string }
  ): Promise<{ message?: string }> => {
    try {
      const response = await Axios.post(
        `/mgmt/v1/deliveries/${id}/cancel`,
        payload
      );
      return response.data?.data ?? response.data ?? {};
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Retry a FAILED delivery — resets it to ALLOCATING for another attempt.
   * Its shipment and outbound request are reset synchronously in the same
   * request; a failed logistic reset aborts the retry (502), nothing changes.
   * POST /mgmt/v1/deliveries/:id/retry
   */
  retry: async (
    id: string,
    payload: { reasonCode: string; notes: string }
  ): Promise<{ message?: string }> => {
    try {
      const response = await Axios.post(
        `/mgmt/v1/deliveries/${id}/retry`,
        payload
      );
      return response.data?.data ?? response.data ?? {};
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** POST /v1/deliveries/:deliveryId/assign-driver/:driverId */
  assignDriver: async (
    deliveryId: string,
    driverId: string | number,
    payload: { notes: string; reasonCode: string | null }
  ): Promise<{ message?: string }> => {
    try {
      const response = await Axios.post(
        `/v1/deliveries/${deliveryId}/assign-driver/${driverId}`,
        payload
      );
      return response.data?.data ?? response.data ?? {};
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Bulk allocate — moves QUEUEING deliveries forward in the pipeline.
   * POST /mgmt/v1/deliveries/bulk-allocate
   */
  bulkAllocate: async (
    deliveryUIDs: string[]
  ): Promise<{ message?: string }> => {
    try {
      const response = await Axios.post('/v1/deliveries/bulk/allocate', {
        deliveryUIDs,
      });
      return response.data?.data ?? response.data ?? {};
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Bulk status update — used by Mark Complete / Mark Failed /
   * Mark Returned / Mark Failed Return.
   *
   * PATCH /mgmt/v1/deliveries/bulk
   */
  bulkUpdate: async (
    deliveries: Array<{
      deliveryUID: string;
      status: DeliveryStatus;
      notes?: string;
      reasonCode?: string;
    }>
  ): Promise<{ message?: string }> => {
    try {
      const response = await Axios.patch('/v1/deliveries/bulk', {
        deliveries,
      });
      return response.data?.data ?? response.data ?? {};
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },
};
